import { Injectable } from '@angular/core';

import { signIn, signOut, resetPassword, confirmResetPassword, confirmSignIn, getCurrentUser, AuthError, fetchAuthSession } from 'aws-amplify/auth';

export enum AuthSteps {
  CONFIRM_SIGN_UP = 'CONFIRM_SIGN_UP',
  NEW_PASSWORD_REQUIRED = 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
  MFA_SELECTION = 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION',
  SMS_MFA = 'CONFIRM_SIGN_IN_WITH_SMS_MFA_CODE',
  DONE = 'DONE'
}

export enum ResetPasswordStep {
  CONFIRM_RESET_PASSWORD_WITH_CODE = 'CONFIRM_RESET_PASSWORD_WITH_CODE',
  NOT_AUTHORIZED = 'NOT_AUTHORIZED',
  DONE = 'DONE'
}

export enum CognitoError {
  UserNotFound = 'UserNotFoundException',
  NotAuthorized = 'NotAuthorizedException',
  UserNotConfirmed = 'UserNotConfirmedException',
  PasswordResetRequired = 'PasswordResetRequiredException',
  InvalidPassword = 'InvalidPasswordException',
  InvalidParameter = 'InvalidParameterException',
  CodeMismatch = 'CodeMismatchException',
  ExpiredCode = 'ExpiredCodeException',
  LimitExceeded = 'LimitExceededException',
  TooManyRequests = 'TooManyRequestsException',
  TooManyFailedAttempts = 'TooManyFailedAttemptsException',
  UsernameExists = 'UsernameExistsException',
  AliasExists = 'AliasExistsException',
  CodeDeliveryFailure = 'CodeDeliveryFailureException',
  MFAMethodNotFound = 'MFAMethodNotFoundException',
  InvalidSession = 'NotAuthorizedException',
  NetworkError = 'NetworkError'
}

export const MIN_PASSWORD_LENGTH = 8;

@Injectable({
  providedIn: 'root'
})
export class CognitoService {

  constructor() {}

  async authenticateUser(username: string, password: string) {
    try {
      return await signIn({ username, password });
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async completeNewPasswordChallenge(newPassword: string) {
    try {
      return await confirmSignIn({ challengeResponse: newPassword });
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async forgotPassword(username: string) {
    try {
      return await resetPassword({ username });
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async confirmPassword(username: string, confirmationCode: string, newPassword: string) {
    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async logout() {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  async getCurrentUser() {
    try {
      return await getCurrentUser();
    } catch {
      return null;
    }
  }

  public async getUserGroups(): Promise<string[]> {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken;
      if (accessToken) {
        const payload = accessToken.payload;
        const groups = payload['cognito:groups'];
        if (Array.isArray(groups) && groups.every(item => typeof item === 'string')) {
          return groups as string[];
        }
      }
      return [];
    } catch (error) {
      console.error('Error getting user groups: ', error);
      return [];
    }
  }

  async getSession() {
    try {
      return await fetchAuthSession();
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  private handleError(error: any) {
    if (error instanceof AuthError) {
      console.error(`Auth Error [${error.name}]: ${error.message}`);
    }
  }

}